'use client';

/**
 * One caller-owned task per screen. The consumer owns validation, step order,
 * review content and the transition to a result after a successful write.
 */

import React from 'react';
import { AlertCircle, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { PageTitle } from '../typography';
import type { FieldDensity } from '../input';
import { Button } from '../button';
import { StickyActions } from '../sticky-actions';
import { ActionGroup } from '../action-group';

export interface WizardStep {
  /** Stable id for the step. */
  id: string;
  kind?: 'task' | 'review' | 'result';
  /** Presentation only; does not change navigation or validation. */
  state?: 'not-started' | 'current' | 'complete' | 'needs-attention';
  /** Display label for the indicator chip. */
  label: string;
  /** Step body content (the consumer's form fields). */
  content: React.ReactNode;
  /**
   * Whether this step's data is valid + the user can advance to the
   * next step. When false, the Next/Submit button is disabled.
   */
  canAdvance?: boolean;
  /**
   * Optional description shown under the indicator when this step is
   * active.
   */
  description?: React.ReactNode;
}

export interface WizardProps {
  /** Ordered list of steps. */
  steps: ReadonlyArray<WizardStep>;
  /** Zero-based index of the current step. */
  currentStepIndex: number;
  /** Set of step indices the user has visited (controls click-to-jump). */
  visitedStepIndices: ReadonlySet<number>;
  /** Move to a different step (used by Back / Next / chip-click). */
  onStepChange: (newIndex: number) => void;
  /** Final-step submit handler. Replaces Next on the last step. */
  onSubmit: () => void;
  /** Optional Save draft handler — adds a "Save as draft" button. */
  onSaveDraft?: () => void;
  /** Optional Cancel handler — adds a "Cancel" button on the left. */
  onCancel?: () => void;
  /**
   * Loading flag — disables all nav buttons while a save / submit is
   * in flight.
   */
  loading?: boolean;
  /** Title shown above the indicator. */
  title?: string;
  className?: string;
  density?: FieldDensity;
}

export function Wizard({
  steps,
  currentStepIndex,
  visitedStepIndices,
  onStepChange,
  onSubmit,
  onSaveDraft,
  onCancel,
  loading,
  title,
  className,
  density = 'productive',
}: WizardProps) {
  const current = steps[currentStepIndex];
  if (!current) return null;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;
  const canAdvance = current.canAdvance ?? true;

  const surface =
    density === 'comfort' ? 'rounded-card-comfort p-7 shadow-card-comfort' : 'rounded-card p-5';

  return (
    <div className={cn('flex flex-col gap-4', className)} data-wizard>
      {/* Header + indicator */}
      <header className={cn('border border-border bg-surface-sheet', surface)}>
        {title && (
          <PageTitle className="mb-4 font-body text-card-title font-semibold text-text-heading">
            {title}
          </PageTitle>
        )}
        <ol role="list" aria-label="Wizard steps" className="flex flex-wrap items-center gap-2">
          {steps.map((step, idx) => {
            const isActive = idx === currentStepIndex;
            const state =
              step.state ??
              (isActive ? 'current' : idx < currentStepIndex ? 'complete' : 'not-started');
            const isComplete = state === 'complete';
            const isVisited = visitedStepIndices.has(idx);
            const isClickable = isVisited && !isActive && !loading;
            return (
              <li
                key={step.id}
                data-step-state={state}
                className="flex min-w-0 flex-wrap items-center gap-2"
              >
                <button
                  type="button"
                  onClick={() => (isClickable ? onStepChange(idx) : undefined)}
                  disabled={!isClickable}
                  aria-current={isActive ? 'step' : undefined}
                  aria-label={`${idx + 1}. ${step.label}, ${state.replaceAll('-', ' ')}`}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-caption font-semibold transition',
                    state === 'needs-attention'
                      ? 'border-status-warning-border bg-status-warning-bg text-status-warning-fg'
                      : isActive
                        ? 'border-focus bg-action text-action-foreground'
                        : isComplete
                          ? 'border-control-border bg-selected text-selected-foreground'
                          : 'border-border bg-surface-sheet text-text-muted',
                    isClickable &&
                      'hover:bg-surface-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                    !isClickable && !isActive && 'cursor-default',
                  )}
                >
                  <span className="font-mono text-caption">{idx + 1}</span>
                  {isComplete && <Check className="h-4 w-4" aria-hidden="true" />}
                  {state === 'needs-attention' && (
                    <AlertCircle className="h-4 w-4" aria-hidden="true" />
                  )}
                  <span className="sr-only">{state.replaceAll('-', ' ')}: </span>
                  <span>{step.label}</span>
                </button>
                {idx < steps.length - 1 && (
                  <span className="h-px w-3 bg-border" aria-hidden="true" />
                )}
              </li>
            );
          })}
        </ol>
        {current.description && (
          <p className="mt-3 text-body text-text-muted">{current.description}</p>
        )}
      </header>

      {/* Step content */}
      <section
        data-wizard-step-kind={current.kind ?? 'task'}
        aria-labelledby={`wizard-step-${current.id}`}
        className={cn('border border-border bg-surface-sheet', surface)}
      >
        <h2
          id={`wizard-step-${current.id}`}
          className="mb-4 font-body text-card-title font-semibold text-text-heading"
        >
          {current.label}
        </h2>
        {current.content}
      </section>

      <StickyActions label="Wizard navigation" role="region">
        <ActionGroup
          secondary={
            <>
              <Button
                type="button"
                variant="secondary"
                density={density}
                onClick={() => onStepChange(currentStepIndex - 1)}
                disabled={isFirstStep || loading}
              >
                <ChevronLeft aria-hidden="true" /> Back
              </Button>
              {onCancel && (
                <Button
                  type="button"
                  variant="secondary"
                  density={density}
                  onClick={onCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
              )}
              {onSaveDraft && (
                <Button
                  type="button"
                  variant="secondary"
                  density={density}
                  onClick={onSaveDraft}
                  disabled={loading}
                >
                  Save as draft
                </Button>
              )}
            </>
          }
          primary={
            <Button
              type="button"
              density={density}
              className={density === 'comfort' ? 'h-12' : 'h-11'}
              onClick={isLastStep ? onSubmit : () => onStepChange(currentStepIndex + 1)}
              disabled={!canAdvance || loading}
            >
              {isLastStep ? (
                'Submit'
              ) : (
                <>
                  Continue <ChevronRight aria-hidden="true" />
                </>
              )}
            </Button>
          }
        />
      </StickyActions>
    </div>
  );
}
